import { useCallback, useEffect, useMemo, useState } from 'react';

import applyService from '../services/applyService';
import { getTenantScopedStorageKey, getCurrentOrgSlug, getCurrentBranchSlug } from '../utils/tenant';

const STORAGE_VERSION = 'v1';
const TTL_MS = 15 * 60 * 1000; // 15 minutes

const buildStorageKey = (context) => {
  const clientSlug = context?.organization_slug || getCurrentOrgSlug();
  const branchSlug = context?.branch_slug;
  return getTenantScopedStorageKey(
    `applyContext:${STORAGE_VERSION}`,
    {
      clientSlug,
      branchSlug,
    }
  );
};

const shouldUseCache = (record) => {
  if (!record) return false;
  const { cachedAt } = record;
  if (!cachedAt) return false;
  return Date.now() - cachedAt < TTL_MS;
};

const readCache = () => {
  const orgSlug = getCurrentOrgSlug();
  const branchSlug = getCurrentBranchSlug();
  const candidateKeys = [
    getTenantScopedStorageKey(`applyContext:${STORAGE_VERSION}`, {
      clientSlug: orgSlug,
      branchSlug: branchSlug || undefined,
    }),
    getTenantScopedStorageKey(`applyContext:${STORAGE_VERSION}`, orgSlug),
  ];

  try {
    for (const key of candidateKeys) {
      if (!key) continue;
      const stored = localStorage.getItem(key);
      if (!stored) continue;
      const parsed = JSON.parse(stored);
      if (parsed && shouldUseCache(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('[useApplyContext] Failed to read cached context', error);
  }
  return null;
};

export const useApplyContext = ({ autoLoad = true } = {}) => {
  const [state, setState] = useState(() => {
    const cached = readCache();
    if (cached) {
      return {
        status: 'ready',
        context: cached.context,
        error: null,
      };
    }
    return {
      status: autoLoad ? 'idle' : 'empty',
      context: null,
      error: null,
    };
  });

  const cacheKey = useMemo(() => {
    if (!state.context) return null;
    return buildStorageKey(state.context);
  }, [state.context]);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    try {
      const context = await applyService.fetchApplyContext();
      const storageRecord = {
        cachedAt: Date.now(),
        context,
      };
      const key = buildStorageKey(context);
      localStorage.setItem(key, JSON.stringify(storageRecord));
      setState({ status: 'ready', context, error: null });
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Unable to load loan officer context right now.';
      setState({ status: 'error', context: null, error: detail });
    }
  }, []);

  const clear = useCallback(() => {
    if (cacheKey) {
      localStorage.removeItem(cacheKey);
    }
    setState({ status: 'empty', context: null, error: null });
  }, [cacheKey]);

  useEffect(() => {
    if (autoLoad && state.status === 'idle') {
      load();
    }
  }, [autoLoad, state.status, load]);

  return {
    context: state.context,
    status: state.status,
    error: state.error,
    load,
    clear,
  };
};

export default useApplyContext;
